/**
 * ProvisioningWizardPage — `/franchise/provisioning`.
 *
 * Multi-step wizard combining the live transitions a freshly-approved
 * franchise needs to walk through:
 *
 *   1. Pick the franchise (filter to APPROVED status)
 *   2. Provision device      → POST /v1/franchise/devices
 *   3. Allocate inventory    → POST /v1/franchise/inventory/franchise/:id/allocate
 *   4. Record training       → POST /v1/franchise/franchises/:id/record-training
 *   5. Draft agreement       → POST /v1/franchise/agreements
 *   6. Activate (go LIVE)    → POST /v1/franchise/franchises/:id/activate
 */
import { type JSX, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Circle } from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import {
  activateFranchise,
  allocateInventory,
  createAgreement,
  createDevice,
  listFranchises,
  recordTraining,
} from '../../lib/api/franchise';
import { DeferredNotice, PageHeader, StatusBadge } from '../shared/StaffUI';

const STEPS = ['pick', 'device', 'inventory', 'training', 'agreement', 'activate'] as const;
type Step = (typeof STEPS)[number];

const ONE_YEAR_MS = 365 * 24 * 60 * 60 * 1000;

export function ProvisioningWizardPage(): JSX.Element {
  const qc = useQueryClient();
  const [step, setStep] = useState<Step>('pick');
  const [franchiseId, setFranchiseId] = useState('');
  const [deviceSerial, setDeviceSerial] = useState('');
  const [stampQty, setStampQty] = useState('200');
  const [stampDenomination, setStampDenomination] = useState('STAMP-N100');
  const [certRef, setCertRef] = useState('');
  const [agreementRev, setAgreementRev] = useState('0.05');
  const [err, setErr] = useState<string | null>(null);

  const candidates = useQuery({
    queryKey: ['franchise', 'provisioning', 'candidates'],
    queryFn: () => listFranchises({ pageSize: 100, status: 'APPROVED' }),
    retry: false,
  });

  const denied = candidates.isError && candidates.error instanceof ApiError &&
    (candidates.error.status === 401 || candidates.error.status === 403);

  const provision = useMutation({
    mutationFn: () =>
      createDevice({ franchiseId, serial: deviceSerial.trim() }),
    onSuccess: () => {
      setErr(null);
      setStep('inventory');
      void qc.invalidateQueries({ queryKey: ['franchise', 'provisioning'] });
    },
    onError: (e: Error) => setErr(e.message),
  });
  const allocate = useMutation({
    mutationFn: () =>
      allocateInventory(franchiseId, {
        itemKind: 'STAMP',
        itemRef: stampDenomination.trim(),
        qty: Number.parseInt(stampQty || '0', 10),
        minThreshold: 50,
      }),
    onSuccess: () => {
      setErr(null);
      setStep('training');
    },
    onError: (e: Error) => setErr(e.message),
  });
  const train = useMutation({
    mutationFn: () => recordTraining(franchiseId, certRef.trim()),
    onSuccess: () => {
      setErr(null);
      setStep('agreement');
    },
    onError: (e: Error) => setErr(e.message),
  });
  const draftAgreement = useMutation({
    mutationFn: () => {
      const now = new Date();
      const until = new Date(now.getTime() + ONE_YEAR_MS);
      return createAgreement({
        franchiseId,
        terms: { type: 'standard', revShare: agreementRev },
        effectiveFrom: now.toISOString(),
        effectiveUntil: until.toISOString(),
      });
    },
    onSuccess: () => {
      setErr(null);
      setStep('activate');
    },
    onError: (e: Error) => setErr(e.message),
  });
  const activate = useMutation({
    mutationFn: () => activateFranchise(franchiseId),
    onSuccess: () => {
      setErr(null);
      setStep('pick');
      setFranchiseId('');
      setDeviceSerial('');
      setCertRef('');
      void qc.invalidateQueries({ queryKey: ['franchise', 'provisioning', 'candidates'] });
    },
    onError: (e: Error) => setErr(e.message),
  });

  const stepIdx = STEPS.indexOf(step);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Provisioning wizard"
        description="Walk an APPROVED franchise through device provisioning → training → LIVE."
      />

      <ol className="bg-white border border-border rounded-xl p-4 grid grid-cols-1 sm:grid-cols-6 gap-2">
        {STEPS.map((key, idx) => (
          <li key={key} className="flex items-center gap-2 text-xs">
            {idx < stepIdx ? (
              <CheckCircle2 className="w-4 h-4 text-nipost-green" />
            ) : (
              <Circle className={`w-4 h-4 ${idx === stepIdx ? 'text-nipost-green' : 'text-muted'}`} />
            )}
            <span className={idx === stepIdx ? 'font-semibold text-gray-900' : 'text-muted'}>
              {labelFor(key)}
            </span>
          </li>
        ))}
      </ol>

      {step === 'pick' ? (
        <section className="bg-white border border-border rounded-xl p-5 space-y-3">
          <h2 className="text-sm font-semibold text-gray-900">1. Pick approved franchise</h2>
          {candidates.isPending ? (
            <p className="text-sm text-muted">Loading…</p>
          ) : denied ? (
            <DeferredNotice title="Permission required">
              <code className="font-mono">Franchise/read</code> required.
            </DeferredNotice>
          ) : candidates.isError ? (
            <DeferredNotice title="Service unreachable">
              {(candidates.error as Error).message}
            </DeferredNotice>
          ) : (candidates.data?.items.length ?? 0) === 0 ? (
            <p className="text-sm text-muted">No APPROVED franchises waiting for provisioning.</p>
          ) : (
            <>
              <select
                value={franchiseId}
                onChange={(e) => setFranchiseId(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm"
              >
                <option value="">Select…</option>
                {candidates.data?.items.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.legalName} ({f.franchiseNumber})
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => setStep('device')}
                disabled={franchiseId.length === 0}
                className="px-3 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark disabled:opacity-50"
              >
                Continue →
              </button>
            </>
          )}
        </section>
      ) : null}

      {step === 'device' ? (
        <section className="bg-white border border-border rounded-xl p-5 space-y-3">
          <h2 className="text-sm font-semibold text-gray-900">2. Provision counter device</h2>
          <input
            type="text"
            value={deviceSerial}
            onChange={(e) => setDeviceSerial(e.target.value)}
            placeholder="Device serial / ref…"
            className="w-full px-3 py-2 border border-border rounded-lg text-sm font-mono"
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setStep('pick')}
              className="px-3 py-2 border border-border rounded-lg text-sm font-medium hover:bg-gray-50"
            >
              Back
            </button>
            <button
              type="button"
              onClick={() => provision.mutate()}
              disabled={provision.isPending || deviceSerial.trim().length === 0}
              className="px-3 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark disabled:opacity-50"
            >
              Provision
            </button>
          </div>
        </section>
      ) : null}

      {step === 'inventory' ? (
        <section
          className="bg-white border border-border rounded-xl p-5 space-y-3"
          data-testid="wizard-step-inventory"
        >
          <h2 className="text-sm font-semibold text-gray-900">3. Allocate stamp inventory</h2>
          <div className="grid grid-cols-2 gap-3">
            <input
              type="text"
              value={stampDenomination}
              onChange={(e) => setStampDenomination(e.target.value)}
              placeholder="Item ref e.g. STAMP-N100"
              className="px-3 py-2 border border-border rounded-lg text-sm font-mono"
            />
            <input
              type="number"
              value={stampQty}
              onChange={(e) => setStampQty(e.target.value)}
              placeholder="Quantity"
              className="px-3 py-2 border border-border rounded-lg text-sm"
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setStep('device')}
              className="px-3 py-2 border border-border rounded-lg text-sm font-medium hover:bg-gray-50"
            >
              Back
            </button>
            <button
              type="button"
              onClick={() => allocate.mutate()}
              disabled={
                allocate.isPending ||
                stampDenomination.trim().length === 0 ||
                Number.parseInt(stampQty || '0', 10) <= 0
              }
              className="px-3 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark disabled:opacity-50"
              data-testid="wizard-allocate-btn"
            >
              Allocate
            </button>
          </div>
        </section>
      ) : null}

      {step === 'training' ? (
        <section className="bg-white border border-border rounded-xl p-5 space-y-3">
          <h2 className="text-sm font-semibold text-gray-900">4. Record training certificate</h2>
          <input
            type="text"
            value={certRef}
            onChange={(e) => setCertRef(e.target.value)}
            placeholder="Certificate ref…"
            className="w-full px-3 py-2 border border-border rounded-lg text-sm font-mono"
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setStep('device')}
              className="px-3 py-2 border border-border rounded-lg text-sm font-medium hover:bg-gray-50"
            >
              Back
            </button>
            <button
              type="button"
              onClick={() => train.mutate()}
              disabled={train.isPending || certRef.trim().length === 0}
              className="px-3 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark disabled:opacity-50"
            >
              Record
            </button>
          </div>
        </section>
      ) : null}

      {step === 'agreement' ? (
        <section
          className="bg-white border border-border rounded-xl p-5 space-y-3"
          data-testid="wizard-step-agreement"
        >
          <h2 className="text-sm font-semibold text-gray-900">5. Draft franchise agreement</h2>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-muted mb-1">
              Revenue share rate
            </label>
            <input
              type="text"
              value={agreementRev}
              onChange={(e) => setAgreementRev(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg text-sm font-mono"
            />
            <p className="text-[11px] text-muted mt-1">
              Decimal fraction (e.g. 0.05 = 5%). Effective period defaults to 1 year.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setStep('training')}
              className="px-3 py-2 border border-border rounded-lg text-sm font-medium hover:bg-gray-50"
            >
              Back
            </button>
            <button
              type="button"
              onClick={() => draftAgreement.mutate()}
              disabled={draftAgreement.isPending || agreementRev.trim().length === 0}
              className="px-3 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark disabled:opacity-50"
              data-testid="wizard-draft-agreement-btn"
            >
              Draft
            </button>
          </div>
        </section>
      ) : null}

      {step === 'activate' ? (
        <section className="bg-white border border-border rounded-xl p-5 space-y-3">
          <h2 className="text-sm font-semibold text-gray-900">6. Activate</h2>
          <p className="text-xs text-muted">
            Transitions <code className="font-mono">TRAINING</code> →{' '}
            <code className="font-mono">LIVE</code>.
          </p>
          <button
            type="button"
            onClick={() => activate.mutate()}
            disabled={activate.isPending}
            className="px-3 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark disabled:opacity-50"
          >
            Go LIVE
          </button>
          <StatusBadge status="TRAINING" />
        </section>
      ) : null}

      {err ? <p className="text-sm text-danger">{err}</p> : null}
    </div>
  );
}

function labelFor(step: Step): string {
  switch (step) {
    case 'pick': return '1. Pick';
    case 'device': return '2. Device';
    case 'inventory': return '3. Inventory';
    case 'training': return '4. Training';
    case 'agreement': return '5. Agreement';
    case 'activate': return '6. Activate';
  }
}
